import { Box, Flex, Text, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { keyframes as emotionKeyframes } from '@emotion/react';
import { useMemo } from 'react';
import { translate } from '../../i18n';
import type { CourtArc } from './useCourtPulseData';

// ---------------------------------------------------------------------------
// Court Pulse — radial venue heartbeat visualization.
//
// One ring per court. Full occupancy = 360deg arc. Empty = thin ghost arc.
// At peak hours (>80% average), the whole circle pulses at 1Hz.
// Pure SVG + CSS animations. No canvas, no WebGL.
// ---------------------------------------------------------------------------

const pulse = emotionKeyframes`
	0%, 100% { transform: scale(1); }
	50% { transform: scale(1.02); }
`;

interface CourtPulseProps {
	arcs: CourtArc[];
	totalOccupancy: number;
	/** Day start timestamp — needed to position arcs on the 24h clock */
	windowStart: Date;
	/** Widget size in px (default: 64) */
	size?: number;
	/** Show court labels on hover (default: true) */
	interactive?: boolean;
}

/** Arc stroke colors — derived from chart palette positions */
function useArcColors() {
	const brand = useColorModeValue('#023F7F', '#3578BF');
	const ghost = useColorModeValue('rgba(44, 42, 38, 0.08)', 'rgba(255, 255, 255, 0.08)');
	return { brand, ghost };
}

/**
 * Calculate SVG arc path for a ring segment.
 * cx/cy = center, r = radius, startAngle/endAngle in degrees (0 = top, clockwise).
 */
function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
	// Clamp to avoid full-circle rendering issues
	const span = Math.min(endDeg - startDeg, 359.99);
	const endClamped = startDeg + span;

	const startRad = ((startDeg - 90) * Math.PI) / 180;
	const endRad = ((endClamped - 90) * Math.PI) / 180;

	const x1 = cx + r * Math.cos(startRad);
	const y1 = cy + r * Math.sin(startRad);
	const x2 = cx + r * Math.cos(endRad);
	const y2 = cy + r * Math.sin(endRad);

	const largeArc = span > 180 ? 1 : 0;

	return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

/** Convert a timestamp to degrees on the 24h clock (00:00 = 0deg = top) */
function timestampToDeg(ts: number, dayStart: number): number {
	const msIntoDay = ts - dayStart;
	const msInDay = 24 * 60 * 60 * 1000;
	return (msIntoDay / msInDay) * 360;
}

export default function CourtPulse({
	arcs,
	totalOccupancy,
	windowStart,
	size = 64,
	interactive = true,
}: CourtPulseProps) {
	const { brand, ghost } = useArcColors();
	const textColor = useColorModeValue('secondaryGray.900', 'white');
	const mutedColor = useColorModeValue('secondaryGray.600', 'gray.400');
	const tooltipBg = useColorModeValue('white', 'navy.700');

	const isPeakHour = totalOccupancy > 0.8;

	// Layout: rings from outside in, evenly spaced
	const svgSize = size;
	const center = svgSize / 2;
	const maxRadius = (svgSize / 2) - 4; // padding
	const ringWidth = Math.max(2, Math.min(5, (maxRadius - 8) / Math.max(arcs.length, 1)));
	const ringGap = Math.max(1, ringWidth * 0.4);

	const dayStartMs = windowStart.getTime();

	const rings = useMemo(() => {
		return arcs.map((arc, idx) => {
			const r = maxRadius - idx * (ringWidth + ringGap);
			if (r < 4) return null;

			const ghostPath = describeArc(center, center, r, 0, 359.99);
			// Draw each booked range at its actual clock position
			const fillPaths = arc.bookedRanges.map(range => {
				const startDeg = timestampToDeg(range.start, dayStartMs);
				const endDeg = timestampToDeg(range.end, dayStartMs);
				return describeArc(center, center, r, startDeg, endDeg);
			}).filter(p => p.length > 0);

			return { arc, r, ghostPath, fillPaths };
		}).filter(Boolean) as Array<{
			arc: CourtArc;
			r: number;
			ghostPath: string;
			fillPaths: string[];
		}>;
	}, [arcs, center, maxRadius, ringWidth, ringGap, dayStartMs]);

	if (arcs.length === 0) {
		return null;
	}

	const widget = (
		<Box
			display='inline-flex'
			alignItems='center'
			justifyContent='center'
			animation={isPeakHour ? `${pulse} 1s ease-in-out infinite` : undefined}
			cursor={interactive ? 'pointer' : 'default'}
		>
			<svg
				width={svgSize}
				height={svgSize}
				viewBox={`0 0 ${svgSize} ${svgSize}`}
				fill='none'
				xmlns='http://www.w3.org/2000/svg'
			>
				{rings.map(({ arc, ghostPath, fillPaths }) => (
					<g key={arc.courtId}>
						{/* Ghost ring (always visible) */}
						<path
							d={ghostPath}
							stroke={ghost}
							strokeWidth={ringWidth}
							strokeLinecap='round'
							fill='none'
						/>
						{/* Booked ranges at actual clock positions */}
						{fillPaths.map((fp, i) => (
							<path
								key={i}
								d={fp}
								stroke={brand}
								strokeWidth={ringWidth}
								strokeLinecap='round'
								fill='none'
								opacity={arc.isLive ? 1 : 0.7}
							/>
						))}
					</g>
				))}
			</svg>
		</Box>
	);

	if (!interactive) return widget;

	// Tooltip with court breakdown
	const tooltipContent = (
		<Flex direction='column' gap='4px' p='4px'>
			<Text fontWeight='700' fontSize='12px'>
				{translate('court_pulse.venue_pulse')} — {Math.round(totalOccupancy * 100)}% {translate('court_pulse.occupied')}
			</Text>
			{arcs.map((arc) => (
				<Flex key={arc.courtId} justify='space-between' gap='12px' fontSize='11px'>
					<Text>{arc.courtName}</Text>
					<Text fontFamily='var(--mono-font, inherit)' fontWeight='500'>
						{Math.round(arc.occupancy * 100)}%
						{arc.isLive && ' ●'}
					</Text>
				</Flex>
			))}
		</Flex>
	);

	return (
		<Tooltip
			label={tooltipContent}
			placement='bottom'
			hasArrow
			bg={tooltipBg}
			color={textColor}
			borderRadius='12px'
			boxShadow='lg'
			px='12px'
			py='8px'
		>
			{widget}
		</Tooltip>
	);
}
